import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';
import { BaseLineDown } from '@/app/components/icons/BaseLineDown';
import { BaseLineUp } from '@/app/components/icons/BaseLineUp';

import { cn } from '@/app/utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectIcon = SelectPrimitive.Icon;

const SelectItemIndicator = SelectPrimitive.ItemIndicator;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    hideArrow?: boolean;
  }
>(({ className, children, hideArrow = false, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'group flex h-8 w-full items-center justify-between rounded-8 border border-system-white12 bg-background px-3 py-1 font-normal text-sm disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
    {!hideArrow && (
      <SelectPrimitive.Icon asChild>
        <BaseLineDown className="group-data-[state=open]:-rotate-180 text-sm text-system-white64 transition-transform ease-out group-data-[state=closed]:duration-100 group-data-[state=open]:duration-300" />
      </SelectPrimitive.Icon>
    )}
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectTriggerWithoutIcon = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-8 w-full items-center justify-between rounded-8 border border-system-white12 bg-background px-3 py-1 font-normal text-sm disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
  </SelectPrimitive.Trigger>
));
SelectTriggerWithoutIcon.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <BaseLineUp className="text-sm" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <BaseLineDown className="text-sm" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    container?: HTMLElement | null;
    scrollUpIcon?: boolean;
    scrollDownIcon?: boolean;
  }
>(
  (
    { className, children, position = 'popper', container, scrollUpIcon = true, scrollDownIcon = true, ...props },
    ref,
  ) => (
    <SelectPrimitive.Portal container={container}>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 relative z-50 max-h-96 overflow-visible rounded-8 bg-system-bg02 px-2 py-2 text-system-white data-[side=bottom]:origin-top data-[side=top]:origin-bottom data-[state=closed]:animate-scaleYOut data-[state=open]:animate-scaleYIn',
          position === 'popper' &&
            'data-[side=left]:-translate-x-1 data-[side=bottom]:mt-1 data-[side=top]:mb-1 data-[side=right]:translate-x-1',
          className,
        )}
        position={position}
        {...props}
      >
        {scrollUpIcon && <SelectScrollUpButton />}
        <SelectPrimitive.Viewport
          className={cn(
            // 长列表需在视区内滚动；勿用 overflow-visible，否则会覆盖 Radix 视区的 overflow:auto
            'max-h-[min(22rem,calc(100dvh-6rem))] min-h-0 overflow-y-auto overflow-x-hidden',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        {scrollDownIcon && <SelectScrollDownButton />}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={cn('px-1 pb-2 text-system-white48 text-xs', className)} {...props} />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    description?: React.ReactNode;
  }
>(({ className, children, description, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative w-full cursor-pointer select-none rounded-6 px-2 py-1.5 text-sm outline-none focus:bg-system-hover01 data-[disabled]:cursor-not-allowed data-[state=checked]:bg-system-hover01 data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText className="flex items-center">{children}</SelectPrimitive.ItemText>
    {description}
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-muted', className)} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// 在当前的 SelectValue 基础上，添加一个自定义的 SelectValueWithImageOnly
const SelectValueWithImageOnly = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value> & {
    selectedItem?: { value: string; image: string; title: string; description?: string };
    activityTip?: React.ReactNode;
    badge?: React.ReactNode;
  }
>(({ className, selectedItem, activityTip, badge, ...props }, ref) => {
  if (selectedItem) {
    return (
      <div className="flex h-6 w-6 items-center gap-2">
        <img src={selectedItem.image} alt={selectedItem.title} className="h-full w-fit flex-shrink-0 rounded-4" />
        <span className="flex items-center whitespace-nowrap text-sm text-system-white">
          {selectedItem.title} {activityTip}
          {badge ? badge : null}
        </span>
      </div>
    );
  }

  return <SelectPrimitive.Value ref={ref} className={className} {...props} />;
});
SelectValueWithImageOnly.displayName = 'SelectValueWithImageOnly';

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectIcon,
  SelectItem,
  SelectItemIndicator,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectTriggerWithoutIcon,
  SelectValue,
  SelectValueWithImageOnly,
};
